import type { Chapter } from './types.ts';
import { renderChapter } from './components/chapter.ts';
import { renderChapterRail, setupChapterRailObserver } from './components/chapter-rail.ts';
import { renderMobileNav, setupMobileNav } from './components/mobile-nav.ts';
import { renderProgressBar, setupProgressBar } from './components/progress-bar.ts';
import { setupSignatureLoader } from './signature-loader.ts';

async function loadChapters(): Promise<Chapter[]> {
  const res = await fetch('chapters.json');
  if (!res.ok) throw new Error(`failed to load chapters.json: ${res.status}`);
  const data: unknown = await res.json();
  if (!Array.isArray(data)) throw new Error('chapters.json is not an array');
  return data as Chapter[];
}

function readingMinutes(chapters: Chapter[]): number {
  const total = chapters.reduce((sum, c) => {
    const text = c.narrationHtml.replace(/<[^>]+>/g, ' ');
    return sum + text.split(/\s+/).filter(Boolean).length;
  }, 0);
  return Math.max(1, Math.round(total / 200));
}

function renderEssayFooter(): HTMLElement {
  const footer = document.createElement('footer');
  footer.className = 'essay-footer';
  footer.innerHTML = `
    <section class="essay-footer-section">
      <h3>Sources</h3>
      <ul>
        <li>Google Quantum AI · Willow announcement, December 2024 · Nature paper on quantum error correction below the surface code threshold</li>
        <li>NIST · Post-Quantum Cryptography standards · FIPS 203 (ML-KEM), FIPS 204 (ML-DSA), FIPS 205 (SLH-DSA), finalized August 2024</li>
        <li>NSA · CNSA 2.0 Cybersecurity Advisory · timeline for National Security Systems migration</li>
        <li>CISA · Post-Quantum Cryptography Initiative · cross-sector guidance</li>
        <li>Scott Aaronson · ongoing analysis of quantum machine-learning data-loading limits</li>
      </ul>
    </section>
    <section class="essay-footer-section essay-footer-meta">
      <p>Built by Fred Nix · <a href="https://github.com/nixfred/quantum">Source on GitHub</a></p>
      <p>Numbers and quotes drawn from the talk-track in <code>content/quantum-talk-track-longform.md</code>.</p>
    </section>
  `;
  return footer;
}

function setupKeyboardNav(chapters: Chapter[]): void {
  function jumpTo(n: number): void {
    const target = chapters[n - 1];
    if (!target) return;
    const el = document.getElementById(`${target.number}-${target.slug}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function currentChapter(): number {
    const links = document.querySelectorAll<HTMLElement>('.chapter-rail-link.is-active');
    const link = links[0];
    if (!link) return 1;
    const n = Number(link.dataset['chapter']);
    return Number.isNaN(n) ? 1 : n;
  }

  window.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const tag = (e.target as HTMLElement | null)?.tagName ?? '';
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (e.key >= '1' && e.key <= '9') {
      e.preventDefault();
      jumpTo(Number(e.key));
      return;
    }
    if (e.key === '0') {
      e.preventDefault();
      jumpTo(10);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      jumpTo(1);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      jumpTo(chapters.length);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === 'j') {
      e.preventDefault();
      jumpTo(Math.min(currentChapter() + 1, chapters.length));
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'k') {
      e.preventDefault();
      jumpTo(Math.max(currentChapter() - 1, 1));
      return;
    }
  });
}

async function main(): Promise<void> {
  const root = document.getElementById('essay');
  if (!root) throw new Error('missing #essay root element');

  const chapters = await loadChapters();

  document.body.prepend(renderProgressBar());
  document.body.prepend(renderChapterRail(chapters));
  const mobileNav = renderMobileNav(chapters);
  document.body.appendChild(mobileNav);

  for (const chapter of chapters) {
    root.appendChild(renderChapter(chapter));
  }

  const minutes = readingMinutes(chapters);
  const firstOverlay = document.querySelector<HTMLElement>('.chapter:first-of-type .hero-overlay');
  if (firstOverlay) {
    const meta = document.createElement('p');
    meta.className = 'essay-meta';
    meta.textContent = `${chapters.length} chapters · ${minutes} minute read`;
    const heroNum = firstOverlay.querySelector('.hero-num');
    if (heroNum) firstOverlay.insertBefore(meta, heroNum);
    else firstOverlay.prepend(meta);
  }

  document.body.appendChild(renderEssayFooter());

  const rail = document.querySelector<HTMLElement>('.chapter-rail');
  const bar = document.querySelector<HTMLElement>('.progress-bar');
  if (rail) setupChapterRailObserver(rail, chapters);
  if (bar) setupProgressBar(bar);
  setupMobileNav(mobileNav, chapters);
  setupSignatureLoader(chapters);
  setupKeyboardNav(chapters);

  if (window.location.hash) {
    const target = document.getElementById(window.location.hash.slice(1));
    target?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }
}

main().catch((err: unknown) => {
  console.error(err);
  const root = document.getElementById('essay');
  if (root) {
    root.textContent = 'failed to load the essay; see console for details';
  }
});

if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').catch((err: unknown) => {
      console.warn('service worker registration failed', err);
    });
  });
}
